import React, { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { Divider } from 'antd';
import parse from 'html-react-parser';
import Alert from 'components/generalUI/Alert';
import FeatureIcon from 'components/generalUI/FeatureIcon';
import { PurchaseConfirmationData } from 'views/TicketSummary/ticketSummary.viewModel';
import styles from './PurchaseConfirmation.module.scss';

type PurchaseConfirmationProps = PurchaseConfirmationData;

const PurchaseConfirmation: FC<PurchaseConfirmationProps> = (
  props: PurchaseConfirmationProps
) => {
  const { t } = useTranslation();
  const {
    image,
    ticketName,
    features,
    ticketCounts,
    date,
    parkHours,
    aperture,
    aviaryHours,
    contact,
  } = props;

  const renderRow = (label: string, value: string) => (
    <div className={styles.Row}>
      <p className={styles.Label}>{label}</p>
      <span className={styles.Value}>{value}</span>
    </div>
  );

  return (
    <section className={styles.Section}>
      <h2 className={styles.Title}>{t('pages.ticketSummary.confirmation.title')}</h2>

      <div className={styles.ImageBox}>
        <img className={styles.Image} src={image} alt={ticketName} />
      </div>

      <h3 className={styles.TicketName}>{ticketName}</h3>

      {features.length > 0 && (
        <div className={styles.Icons}>
          {features.map((feature, index) => (
            <FeatureIcon
              key={feature.slug}
              icon={feature.slug}
              variant={index % 2 ? 'light' : 'dark'}
              description={feature.name}
            />
          ))}
        </div>
      )}

      <Divider className={styles.Divider} />

      <div className={styles.Details}>
        <div className={styles.Row}>
          <p className={styles.Label}>
            {t('pages.ticketSummary.confirmation.ticketQuantity')}
          </p>
          <div className={styles.Counts}>
            {ticketCounts.map((count) => (
              <span className={styles.Value} key={count}>
                {count}
              </span>
            ))}
          </div>
        </div>

        {renderRow(t('pages.ticketSummary.confirmation.date'), date)}
        {parkHours &&
          renderRow(t('pages.ticketSummary.confirmation.parkHours'), parkHours)}
        {aviaryHours &&
          renderRow(t('pages.ticketSummary.confirmation.aviaryHours'), aviaryHours)}
      </div>

      {aperture && (
        <Alert
          className={styles.Alert}
          description={parse(
            t('pages.ticketSummary.confirmation.alertText', { aperture })
          )}
        />
      )}

      <Divider className={styles.Divider} />

      <div className={styles.Details}>
        <h3 className={styles.SectionTitle}>
          {t('pages.ticketSummary.confirmation.contactSection')}
        </h3>
        {renderRow(t('pages.ticketSummary.confirmation.document'), contact.document)}
        {renderRow(t('pages.ticketSummary.confirmation.fullName'), contact.fullName)}
        {renderRow(t('pages.ticketSummary.confirmation.email'), contact.email)}
        {renderRow(t('pages.ticketSummary.confirmation.phone'), contact.phone)}
      </div>
    </section>
  );
};

export default PurchaseConfirmation;
